import { Stack, useTheme } from "@mui/material";
import { useId, type ReactNode } from "react";
import { Controller, type Control, type FieldPath, type FieldValues, type PathValue } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FoodsSelect, FoodsTextInput, Typography } from "@foodpilot/foods";

import { FoodsFieldLabel } from "src/components/EntityForm/FoodsFieldPrimitives";
import { FoodsSelectTriggerAdapter } from "src/components/EntityForm/FoodsSelectTriggerAdapter";

type FieldStackProps = {
    label?: string;
    required?: boolean;
    labelId?: string;
    children: ReactNode;
};

/** Vertical stack with the section label rendered with the design-system typography. */
export const FieldStack = ({ label, required = false, labelId, children }: FieldStackProps) => {
    const theme = useTheme();
    return (
        <Stack gap={theme.spacing(1)}>
            {label !== undefined && <FoodsFieldLabel id={labelId} label={label} required={required} />}
            {children}
        </Stack>
    );
};

type FieldErrorProps = { id?: string; message: string; isError?: boolean; announceError?: boolean };

/** Inline error / helper text shown below a field. */
export const FieldError = ({ id, message, isError = true, announceError = false }: FieldErrorProps) => {
    const theme = useTheme();
    return (
        <Typography
            id={id}
            role={announceError && isError ? "alert" : undefined}
            variant="small"
            sx={{ color: isError ? theme.custom.red[600] : theme.custom.grey[1400] }}
        >
            {message}
        </Typography>
    );
};

type RhfFoodsTextFieldProps<TForm extends FieldValues, TName extends FieldPath<TForm>> = {
    control: Control<TForm>;
    name: TName;
    label?: string;
    required?: boolean;
    helperText?: string;
    errorMessage?: string;
    unit?: string;
    type?: "text" | "number";
    min?: number;
    max?: number;
    step?: number;
    disabled?: boolean;
    /** Called before the form value changes when another field must stay in sync for validation. */
    onBeforeChange?: (next: string | number | null) => void;
    /** Called after the form value has been updated, with the parsed value. */
    onAfterChange?: (next: string | number | null) => void;
};

/** RHF wrapper around `FoodsTextInput` that handles string / number conversion. */
export const RhfFoodsTextField = <TForm extends FieldValues, TName extends FieldPath<TForm>>({
    control,
    name,
    label,
    required = false,
    helperText,
    errorMessage,
    unit,
    type = "text",
    min,
    max,
    step,
    disabled = false,
    onBeforeChange,
    onAfterChange,
}: RhfFoodsTextFieldProps<TForm, TName>) => (
    <Controller
        control={control}
        name={name}
        render={({ field }) => {
            const value = field.value;
            const stringValue =
                value === null || value === undefined ? ""
                : typeof value === "number" ? String(value)
                : String(value);

            return (
                <FieldStack label={label} required={required}>
                    <FoodsTextInput
                        value={stringValue}
                        onChange={(next) => {
                            if (type === "number") {
                                const parsed = next === "" ? null : Number(next);
                                onBeforeChange?.(parsed);
                                field.onChange(parsed as PathValue<TForm, TName>);
                                onAfterChange?.(parsed);
                            } else {
                                onBeforeChange?.(next);
                                field.onChange(next as PathValue<TForm, TName>);
                                onAfterChange?.(next);
                            }
                        }}
                        error={Boolean(errorMessage)}
                        disabled={disabled}
                        unit={unit}
                        ExtraInputProps={
                            type === "number" ? { type: "number", inputProps: { min, max, step } } : undefined
                        }
                    />
                    {(errorMessage || helperText) && (
                        <FieldError message={errorMessage ?? helperText ?? ""} isError={Boolean(errorMessage)} />
                    )}
                </FieldStack>
            );
        }}
    />
);

type RhfFoodsSelectProps<TForm extends FieldValues, TName extends FieldPath<TForm>, TOption> = {
    appearance?: "legacy" | "product-v2";
    control: Control<TForm>;
    name: TName;
    options: TOption[];
    getId: (option: TOption) => number | string;
    getName: (option: TOption) => string;
    onAfterChange?: (option: TOption | null) => void;
    label?: string;
    required?: boolean;
    placeholder: string;
    searchPlaceholder?: string;
    emptyOptionsText?: string;
    noResultText?: string;
    errorMessage?: string;
    helperText?: string;
    disabled?: boolean;
};

/** RHF wrapper around `FoodsSelect` that stores the option id in the form. */
export const RhfFoodsSelect = <TForm extends FieldValues, TName extends FieldPath<TForm>, TOption>(
    props: RhfFoodsSelectProps<TForm, TName, TOption>,
) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const generatedId = useId();
    const {
        appearance = "legacy",
        control,
        name,
        options,
        getId,
        getName,
        onAfterChange,
        label,
        required = false,
        placeholder,
        searchPlaceholder,
        emptyOptionsText,
        noResultText,
        errorMessage,
        helperText,
        disabled = false,
    } = props;
    const isProductV2 = appearance === "product-v2";
    const labelId = isProductV2 && label !== undefined ? `packaging-select-label-${generatedId}` : undefined;
    const descriptionId =
        isProductV2 && (errorMessage || helperText) ? `packaging-select-description-${generatedId}` : undefined;

    return (
        <Controller
            control={control}
            name={name}
            render={({ field }) => {
                const selected = options.find((option) => getId(option) === field.value) ?? null;
                const select = (
                    <FoodsSelect<TOption>
                        selectedOption={selected}
                        options={options}
                        getId={getId}
                        getName={getName}
                        onChange={(value) => {
                            const nextId =
                                value ? (getId(value) as PathValue<TForm, TName>) : (null as PathValue<TForm, TName>);
                            field.onChange(nextId);
                            onAfterChange?.(value);
                        }}
                        disabled={disabled}
                        textOptions={{
                            placeholder,
                            searchPlaceholder:
                                searchPlaceholder ?? t("packaging.select.search_placeholder", "Search..."),
                            emptyOptionsText:
                                emptyOptionsText ?? t("packaging.select.empty_options", "No options available"),
                            noResult: noResultText ?? t("packaging.select.no_result", "No result"),
                        }}
                        BoxProps={{ width: "100%" }}
                        BoxOptionsProps={appearance === "legacy" ? { width: theme.spacing(56.25) } : undefined}
                    />
                );

                return (
                    <FieldStack label={label} required={required} labelId={labelId}>
                        {isProductV2 ?
                            <FoodsSelectTriggerAdapter
                                labelId={labelId}
                                descriptionId={descriptionId}
                                invalid={Boolean(errorMessage)}
                            >
                                {select}
                            </FoodsSelectTriggerAdapter>
                        :   select}
                        {(errorMessage || helperText) && (
                            <FieldError
                                id={descriptionId}
                                message={errorMessage ?? helperText ?? ""}
                                isError={Boolean(errorMessage)}
                                announceError={isProductV2}
                            />
                        )}
                    </FieldStack>
                );
            }}
        />
    );
};
